import { useInstallContext } from './InstallContext.js';
import './InstallAdmin.css';
import * as Dialog from '@radix-ui/react-dialog';
import { Download } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button, useDianaUIPortal } from '../../ui/index.js';
import { CompactDialogContent } from '../../ui/CompactDialogContent.js';

export function InstallAdmin({ demo = false }: { demo?: boolean }) {
  const portal = useDianaUIPortal();
  const [open, setOpen] = useState(false);
  const { installed, available, takePrompt } = useInstallContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const gate = useRef(false);
  useEffect(() => {
    if (installed) setOpen(false);
  }, [installed]);
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (installed && !demo) return null;
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button type="button" className="install-admin-entry">
          <Download size={16} aria-hidden="true" />
          <span>
            {demo ? 'Preview installation' : 'Install app'}
            <small>Open Admin from your home screen</small>
          </span>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="dui-dialog-overlay" />
        <CompactDialogContent
          title="Install Admin"
          onClose={() => setOpen(false)}
          closeLabel="Close installation"
          busy={busy}
        >
          <p>Open Perova Photo Admin in its own window. Your usual sign-in still applies.</p>
          {demo ? <p>This preview does not install anything.</p> : null}
          <ol>
            <li>{ios ? 'Open this page in Safari and tap Share.' : 'Open your browser menu.'}</li>
            <li>
              {ios
                ? 'Choose Add to Home Screen, then Add.'
                : 'Choose Install app or Add to Home Screen.'}
            </li>
            <li>Open the new icon and sign in if asked.</li>
          </ol>
          {available && !demo ? (
            <Button
              variant="primary"
              loading={busy}
              onClick={async () => {
                if (gate.current) return;
                const request = takePrompt();
                if (!request) return;
                gate.current = true;
                setBusy(true);
                setError('');
                try {
                  await request.prompt();
                  await request.userChoice;
                } catch {
                  setError('Use your browser menu to add Admin to your home screen.');
                } finally {
                  gate.current = false;
                  setBusy(false);
                }
              }}
            >
              Install
            </Button>
          ) : null}
          {busy ? <p role="status">Opening installation…</p> : null}
          {error ? <p role="alert">{error}</p> : null}
        </CompactDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
